import React, { useState, useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import Sidebar from '../menu/sidebar/sidebarWeb';
import UserAuthentication from '../userAuthentication';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Drawer,
  ButtonBase,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import TopBar from '../topbar/TopBar';
import UserActions from '../topbar/UserActions';
import 'dayjs/locale/pt-br';
import { useThemeContext } from '../../context/ThemeContext';
import OfflineBanner from '../connectivity/OfflineBanner';
import logoSigo from '../../assets/logo_sigo.png';
import { APP_VERSION } from '../../version';

const componentOverrides = {
  MuiButton: {
    defaultProps: { size: 'medium' },
    styleOverrides: { root: { textTransform: 'none' } },
  },
  MuiTextField: { defaultProps: { size: 'small', margin: 'dense' } },
  MuiSelect: { defaultProps: { size: 'small' } },
  MuiFormControl: { defaultProps: { margin: 'dense', size: 'small' } },
  MuiDatePicker: { defaultProps: { slotProps: { textField: { size: 'small' } } } },
  MuiTimePicker: { defaultProps: { slotProps: { textField: { size: 'small' } } } },
  MuiTimeField: { defaultProps: { slotProps: { textField: { size: 'small' } } } },
};

export default function Layout({ children }) {
  const [isSidebarOpen, setSidebarOpen] = useState(true);
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { darkMode } = useThemeContext();
  const location = useLocation();
  const navigate = useNavigate();

  // Fecha o menu lateral mobile ao navegar para outra rota.
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const theme = useMemo(
    () =>
      createTheme({
        palette: { mode: darkMode ? 'dark' : 'light' },
        components: componentOverrides,
      }),
    [darkMode],
  );

  // Apenas smartphones (largura ~390-430px) usam o layout mobile.
  // Tablets em diante (>= 600px, ex.: 768/820px) usam o layout web.
  const isWebLayout = useMediaQuery(theme.breakpoints.up('sm'));

  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
        {isWebLayout ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <OfflineBanner />
            <div className="d-flex bg-body" style={{ flex: 1 }}>
              <UserAuthentication />
              <Sidebar
                onSelect={() => {}}
                isOpen={isSidebarOpen}
                toggleSidebar={() => setSidebarOpen(!isSidebarOpen)}
              />

              <div className="flex-grow-1 d-flex flex-column">
                <TopBar />
                <div className="p-4">
                  <Container fluid>{children}</Container>
                </div>
              </div>
            </div>
          </Box>
        ) : (
          <Box
            sx={{
              minHeight: '100vh',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: 'background.default',
            }}
          >
            <OfflineBanner />
            <UserAuthentication />

            <AppBar
              position="static"
              sx={{
                backgroundColor: 'background.paper',
                color: 'text.primary',
                boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
              }}
            >
              <Toolbar>
                <IconButton
                  edge="start"
                  color="inherit"
                  aria-label="menu"
                  onClick={() => setMobileMenuOpen(true)}
                >
                  <MenuIcon />
                </IconButton>
                <ButtonBase
                  onClick={() => navigate('/home')}
                  aria-label="Ir para o início"
                  sx={{ ml: 1, borderRadius: 1, p: 0.5 }}
                >
                  <Box component="img" src={logoSigo} alt="SIGO" sx={{ height: 24 }} />
                </ButtonBase>
                <Box sx={{ flexGrow: 1 }} />
                <UserActions />
              </Toolbar>
            </AppBar>

            <Drawer
              anchor="left"
              open={isMobileMenuOpen}
              onClose={() => setMobileMenuOpen(false)}
              PaperProps={{ sx: { width: 220 } }}
            >
              <Sidebar onSelect={() => {}} isOpen toggleSidebar={() => setMobileMenuOpen(false)} />
            </Drawer>

            <Box component="main" sx={{ flexGrow: 1, p: 2 }}>
              <Container fluid>{children}</Container>
            </Box>

            <Typography variant="caption" align="center" sx={{ color: 'text.secondary', py: 1 }}>
              {APP_VERSION}
            </Typography>
          </Box>
        )}
      </LocalizationProvider>
    </ThemeProvider>
  );
}
